import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import { Typography, Paper, Chip } from "@mui/material";
import {
  Verified as VerifiedIcon,
  Star as StarIcon,
  Diamond as DiamondIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled components
const NotificationContainer = styled(Paper)(({ theme, tierColor }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2),
//   backgroundColor: tierColor
//     ? `${tierColor}0A`
//     : theme.palette.background.paper,
  borderLeft: tierColor ? `4px solid ${tierColor}` : "4px solid transparent",
  boxShadow: theme.shadows[1],
  position: "relative",
  overflow: "hidden",
}));

export default function NotificationAlert({
  notificationBarOpen,
  setNotificationBarOpen,
  message,
  status,
  type,
  tier,
  paymentType,
  fromTier,
  amount,
}) {
  // Auto close notification after 10 seconds
  useEffect(() => {
    if (notificationBarOpen) {
      const timer = setTimeout(() => {
        setNotificationBarOpen(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [notificationBarOpen, setNotificationBarOpen]);

  // Get tier color
  const getTierColor = (tierName) => {
    switch (tierName) {
      case "standard":
        return "#1976d2"; // Blue
      case "premium":
        return "#d44402"; // Orange
      case "elite":
        return "#aa00ff"; // Purple
      default:
        return null;
    }
  };

  // Get tier icon
  const getTierIcon = (tierName) => {
    switch (tierName) {
      case "standard":
        return (
          <VerifiedIcon
            sx={{ fontSize: "2rem", color: getTierColor("standard") }}
          />
        );
      case "premium":
        return (
          <StarIcon sx={{ fontSize: "2rem", color: getTierColor("premium") }} />
        );
      case "elite":
        return (
          <DiamondIcon
            sx={{ fontSize: "2rem", color: getTierColor("elite") }}
          />
        );
      default:
        return <InfoIcon sx={{ fontSize: "2rem", color: "#757575" }} />;
    }
  };

  // Get status icon
  const getStatusIcon = (statusType) => {
    switch (statusType) {
      case "success":
        return (
          <CheckCircleIcon sx={{ fontSize: "2rem", color: "success.main" }} />
        );
      case "error":
        return <ErrorIcon sx={{ fontSize: "2rem", color: "error.main" }} />;
      case "info":
        return <InfoIcon sx={{ fontSize: "2rem", color: "info.main" }} />;
      default:
        return <InfoIcon sx={{ fontSize: "2rem", color: "info.main" }} />;
    }
  };

  // Determine if this is a membership change notification
  const isMembershipNotification =
    type === "membership" && status === "success" && tier;

  // Determine if this is a payment notification
  const isPaymentNotification = type === "payments" && amount;

  // Get appropriate notification content based on the notification type
  const renderNotificationContent = () => {
    // Membership notifications
    if (isMembershipNotification) {
      const tierColor = getTierColor(tier);
      const isUpgrade =
        fromTier &&
        ["basic", "standard", "premium", "elite"].indexOf(tier) >
          ["basic", "standard", "premium", "elite"].indexOf(fromTier);
      const isDowngrade =
        fromTier &&
        ["basic", "standard", "premium", "elite"].indexOf(tier) <
          ["basic", "standard", "premium", "elite"].indexOf(fromTier);

      let title = "";
      let description = "";

      if (paymentType === "subscription_new") {
        title = `Welcome to ${tier.charAt(0).toUpperCase() + tier.slice(1)}!`;
        description = `You now have access to all ${tier} membership benefits.`;
      } else if (paymentType === "subscription_change") {
        if (isUpgrade) {
          title = `Upgraded to ${
            tier.charAt(0).toUpperCase() + tier.slice(1)
          }!`;
          description = `You've successfully upgraded from ${fromTier} to ${tier}.`;
        } else if (isDowngrade) {
          title = `Changed to ${tier.charAt(0).toUpperCase() + tier.slice(1)}`;
          description = `Your membership has been changed from ${fromTier} to ${tier}.`;
        } else {
          title = `Membership Updated`;
          description = `Your membership has been successfully updated.`;
        }
      } else if (paymentType === "subscription_canceled") {
        title = `Membership Canceled`;
        description = `Your ${tier} membership will remain active until the end of the billing period.`;
      } else if (paymentType === "subscription_renewal") {
        title = `${
          tier.charAt(0).toUpperCase() + tier.slice(1)
        } Membership Renewed`;
        description = `Your membership has been successfully renewed.`;
      }

      return (
        <NotificationContainer tierColor={tierColor}>
          <Box
            sx={{
              mr: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {getTierIcon(tier)}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            {fromTier && (
              <Chip
                size="small"
                label={
                  isUpgrade ? "Upgrade" : isDowngrade ? "Downgrade" : "Change"
                }
                color={isUpgrade ? "success" : isDowngrade ? "warning" : "info"}
                sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Box>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setNotificationBarOpen(false)}
            sx={{ alignSelf: "flex-start" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </NotificationContainer>
      );
    }

    // Payment notifications
    else if (isPaymentNotification) {
      const isSuccess = status === "success";
      return (
        <NotificationContainer
          tierColor={isSuccess ? "success.main" : "error.main"}
          sx={{
            borderLeft: `4px solid ${
              isSuccess ? "success.main" : "error.main"
            }`,
          }}
        >
          <Box sx={{ mr: 2 }}>{getStatusIcon(status)}</Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {isSuccess ? "Payment Successful" : "Payment Failed"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSuccess
                ? `Your payment of $${amount} was successfully processed.`
                : `Your payment of $${amount} could not be processed.`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => setNotificationBarOpen(false)}
            sx={{ alignSelf: "flex-start" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </NotificationContainer>
      );
    }

    // Default notification
    else {
      return (
        <Alert
          severity={status}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setNotificationBarOpen(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message}
        </Alert>
      );
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Collapse in={notificationBarOpen}>
        {renderNotificationContent()}
      </Collapse>
    </Box>
  );
}
